import { FC } from 'react';

import MicIcon from '@mui/icons-material/Mic';
import { Mic, MicOff } from 'lucide-react';

import { Button } from '../TailwindComponents';
import { StopButtonProps } from './types';
import { cn } from '../TailwindComponents/lib/utils';

// Custom Stop Button Component
export const StopButton: FC<StopButtonProps> = ({ className }) => (
  <div className={cn('relative', className)}>
    {/* White inner square - this is the icon */}
    <div className="w-3 h-3 bg-white rounded-[3px]"></div>
  </div>
);

interface VoiceButtonProps {
  isRecording: boolean;
  isTranscribing: boolean;
  startRecording: () => void;
  stopRecording: () => void;
  variant?: 'default' | 'stop-button';
}

export const VoiceButton: FC<VoiceButtonProps> = ({
  isRecording,
  isTranscribing,
  startRecording,
  stopRecording,
  variant,
}) => {
  const handleClick = isRecording ? stopRecording : startRecording;
  const title = isRecording ? 'Stop recording' : 'Start recording';

  if (variant === 'stop-button') {
    return (
      <Button
        disabled={isTranscribing}
        className={cn(
          `flex items-center gap-2 p-3 rounded-md font-medium transition-all duration-200 shadow-none ${
            isRecording
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : isTranscribing
              ? 'bg-gray-300 text-white cursor-not-allowed dark:bg-slate-600'
              : 'bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-100'
          }`,
        )}
        type="button"
        onClick={handleClick}
        title={title}
      >
        {isTranscribing ? (
          <div className="animate-spin rounded-full w-4 h-4 border-2 border-gray-600 border-t-transparent"></div>
        ) : isRecording ? (
          <StopButton className="pr-[2px] pl-[2px]" />
        ) : (
          <MicIcon
            fontSize="large"
            className="scale-150 text-gray-900 dark:text-slate-100"
          />
        )}
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      disabled={isTranscribing}
      className={cn(
        `flex items-center gap-2 p-3 rounded-md font-medium transition-all duration-200 ${
          isRecording
            ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg animate-pulse'
            : isTranscribing
            ? 'bg-gray-300 text-white cursor-not-allowed'
            : 'bg-gray-400 hover:bg-gray-500 shadow-lg hover:shadow-xl'
        }`,
      )}
      type="button"
      onClick={handleClick}
      title={title}
    >
      {isTranscribing ? (
        <div className="animate-spin rounded-full w-4 h-4 border-2 border-white border-t-transparent"></div>
      ) : isRecording ? (
        <MicOff />
      ) : (
        <Mic />
      )}
    </Button>
  );
};

interface StatusTextProps {
  isRecording: boolean;
  isTranscribing: boolean;
  variant?: 'default' | 'stop-button';
}

export const StatusText: FC<StatusTextProps> = ({
  isRecording,
  isTranscribing,
  variant,
}) => {
  if (variant === 'stop-button') {
    return null;
  }

  if (isTranscribing) {
    return <>...</>;
  } else if (isRecording) {
    return <>Listening...</>;
  }
  return null;
};
